import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { 
  ArrowLeft,
  Building2, 
  Save,
  Users,
  Trash2,
  Plus,
  ExternalLink,
  Info
} from 'lucide-react'
import { getFirebaseDb } from '@/lib/firebase'
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore'
import { 
  FEATURE_FLAGS, 
  FeatureOverrides, 
  getAutoValue, 
  PlatformSettings 
} from '@/lib/feature-flags'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface Organization {
  id: string
  name: string
  slug: string
  createdAt: Date
  featureOverrides?: FeatureOverrides
  enabledFeatures?: string[] // Legacy field
}

interface Member {
  id: string
  userId: string
  email: string
  role: string
  createdAt: Date
}

export function AdminOrganizationEditPage() {
  const { orgId } = useParams<{ orgId: string }>()
  const navigate = useNavigate()
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [featureOverrides, setFeatureOverrides] = useState<FeatureOverrides>({})
  const [platformDefaults, setPlatformDefaults] = useState<{ [key: string]: boolean }>({})
  const [orgName, setOrgName] = useState('')

  useEffect(() => {
    const fetchData = async () => {
      if (!orgId) {
        setLoading(false)
        return
      }
      
      try {
        const db = getFirebaseDb()
        
        // Fetch platform defaults first
        const platformDoc = await getDoc(doc(db, 'platform', 'settings'))
        if (platformDoc.exists()) {
          const platformData = platformDoc.data() as PlatformSettings
          setPlatformDefaults(platformData.featureDefaults || {})
        }
        
        // Fetch organization
        const orgDoc = await getDoc(doc(db, 'organizations', orgId))
        
        if (orgDoc.exists()) {
          const data = orgDoc.data()
          
          // Convert legacy enabledFeatures to featureOverrides if needed
          let overrides: FeatureOverrides = data.featureOverrides || {}
          if (!data.featureOverrides && data.enabledFeatures) {
            // Migrate legacy format: explicitly enabled features become true
            overrides = {}
            for (const feature of FEATURE_FLAGS) {
              if (data.enabledFeatures.includes(feature.id)) {
                overrides[feature.id] = true
              }
              // Features not in enabledFeatures remain as null (Auto)
            }
          }
          
          const org: Organization = {
            id: orgDoc.id,
            name: data.name,
            slug: data.slug,
            createdAt: data.createdAt?.toDate() || new Date(),
            featureOverrides: overrides,
            enabledFeatures: data.enabledFeatures
          }
          setOrganization(org)
          setOrgName(org.name)
          setFeatureOverrides(overrides)
          
          // Fetch members
          const membersSnapshot = await getDocs(collection(db, 'organizations', orgId, 'members'))
          const membersData: Member[] = membersSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date()
          })) as Member[]
          setMembers(membersData)
        }
      } catch (error) {
        console.error('Error fetching organization:', error)
      } finally {
        setLoading(false)
      }
    }
    
    fetchData()
  }, [orgId])

  const handleFeatureOverrideChange = (featureId: string, value: string) => {
    setFeatureOverrides(prev => ({
      ...prev,
      [featureId]: value === 'auto' ? null : value === 'on'
    }))
  }

  const getFeatureOverrideValue = (featureId: string): string => {
    const override = featureOverrides[featureId]
    if (override === null || override === undefined) return 'auto'
    return override ? 'on' : 'off'
  }

  const getEffectiveValue = (featureId: string): boolean => {
    const override = featureOverrides[featureId]
    if (override !== null && override !== undefined) return override
    // Fall back to platform default
    return platformDefaults[featureId] ?? getAutoValue(featureId, platformDefaults)
  }

  const handleSave = async () => {
    if (!orgId) return
    
    setSaving(true)
    try {
      const db = getFirebaseDb()
      await updateDoc(doc(db, 'organizations', orgId), {
        name: orgName,
        featureOverrides: featureOverrides,
        updatedAt: new Date()
      })
      
      // Update local state
      setOrganization(prev => prev ? { ...prev, name: orgName, featureOverrides } : null)
    } catch (error) {
      console.error('Error saving organization:', error)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!organization) {
    return (
      <div className="text-center py-12">
        <Building2 className="mx-auto h-12 w-12 text-muted-foreground" />
        <h3 className="mt-4 text-lg font-semibold">Organization Not Found</h3>
        <Button variant="link" onClick={() => navigate('/admin/organizations')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Organizations
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/organizations')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{organization.name}</h1>
            <p className="text-muted-foreground flex items-center gap-2">
              /{organization.slug}
              <Link to={`/${organization.slug}/assist`} className="hover:text-foreground">
                <ExternalLink className="h-4 w-4" />
              </Link>
            </p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Update organization details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Organization Name</Label>
              <Input
                id="name"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">Slug (URL)</Label>
              <Input
                id="slug"
                value={organization.slug}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">Slug cannot be changed</p>
            </div>
            <div className="space-y-2">
              <Label>Created</Label>
              <p className="text-sm text-muted-foreground">
                {organization.createdAt.toLocaleDateString()} at {organization.createdAt.toLocaleTimeString()}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Members */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Members</CardTitle>
              <CardDescription>{members.length} member(s)</CardDescription>
            </div>
            <Button variant="outline" size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Member
            </Button>
          </CardHeader>
          <CardContent>
            {members.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No members yet</p>
            ) : (
              <div className="space-y-3">
                {members.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-2 rounded-lg border">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                        <Users className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{member.email}</p>
                        <Badge variant="secondary" className="text-xs">{member.role}</Badge>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Feature Flags */}
      <Card>
        <CardHeader>
          <CardTitle>Feature Flags</CardTitle>
          <CardDescription>Configure features for this organization</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-muted/50 border rounded-lg p-3 mb-6">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <strong>Auto</strong> uses the platform default setting. Choose <strong>On</strong> or <strong>Off</strong> to override for this organization.
              </div>
            </div>
          </div>
          <div className="space-y-4">
            {FEATURE_FLAGS.map((feature, index) => (
              <div key={feature.id}>
                {index > 0 && <Separator className="my-4" />}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5 flex-1 mr-4">
                    <Label htmlFor={feature.id} className="text-base font-medium">
                      {feature.name}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Platform default: <Badge variant="outline" className="text-xs ml-1">
                        {platformDefaults[feature.id] !== undefined 
                          ? (platformDefaults[feature.id] ? 'On' : 'Off')
                          : (feature.defaultEnabled ? 'On' : 'Off')}
                      </Badge>
                      {getFeatureOverrideValue(feature.id) === 'auto' && (
                        <span className="ml-2">
                          → Effective: <Badge variant={getEffectiveValue(feature.id) ? 'default' : 'secondary'} className="text-xs ml-1">
                            {getEffectiveValue(feature.id) ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </span>
                      )}
                    </p>
                  </div>
                  <Select 
                    value={getFeatureOverrideValue(feature.id)}
                    onValueChange={(value) => handleFeatureOverrideChange(feature.id, value)}
                  >
                    <SelectTrigger className="w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto</SelectItem>
                      <SelectItem value="on">On</SelectItem>
                      <SelectItem value="off">Off</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive">Danger Zone</CardTitle>
          <CardDescription>Irreversible actions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Delete Organization</p>
              <p className="text-sm text-muted-foreground">
                Permanently delete this organization and all its data
              </p>
            </div>
            <Button variant="destructive">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
